import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  BarChart2,
  Bot,
  CheckCircle,
  CheckCircle2,
  ClipboardList,
  Clock,
  Flame,
  Image,
  Lightbulb,
  Loader2,
  MessageSquareText,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { formatDateTime } from '@/helpers/formatDate';
import { TicketDetail, TicketStatus } from '@packages/database/types';
import { cn } from '@/lib/utils';
import { useApproveTicket, useRejectTicket } from '@/hooks/api/useTickets';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TicketReviewTabProps {
  ticket: TicketDetail;
}

type Severity = 'low' | 'medium' | 'high' | 'critical';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<Severity, { label: string; className: string }> =
  {
    low: {
      label: 'Low',
      className: 'bg-green-500/10  text-green-500  border-green-500/20',
    },
    medium: {
      label: 'Medium',
      className: 'bg-amber-500/10  text-amber-500  border-amber-500/20',
    },
    high: {
      label: 'High',
      className: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    },
    critical: {
      label: 'Critical',
      className: 'bg-red-500/10    text-red-500    border-red-500/20',
    },
  };

const completenessColor = (score: number) => {
  if (score < 3) return 'bg-red-500';
  if (score < 4) return 'bg-amber-500';
  return 'bg-green-500';
};

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyState = ({
  icon: Icon,
  title,
  description,
  pulse = false,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  pulse?: boolean;
}) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center space-y-3">
    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
      <Icon
        className={cn(
          'w-5 h-5 text-muted-foreground',
          pulse && 'animate-pulse',
        )}
      />
    </div>
    <div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-xs">
        {description}
      </p>
    </div>
  </div>
);

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section = ({
  icon: Icon,
  iconClass,
  title,
  children,
}: {
  icon: React.ElementType;
  iconClass: string;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
      <Icon className={cn('w-4 h-4 shrink-0', iconClass)} />
      {title}
    </h3>
    {children}
  </div>
);

// ─── Admin action footer ──────────────────────────────────────────────────────

const AdminActionFooter = ({ ticketId }: { ticketId: string }) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState('');

  const { approveTicketAsync, isApproving } = useApproveTicket();
  const { rejectTicketAsync, isRejecting } = useRejectTicket();

  const isBusy = isApproving || isRejecting;

  const handleVerify = async () => {
    try {
      await approveTicketAsync(ticketId);
      toast.success('Ticket verified successfully.');
    } catch {
      // error handled inside hook via handleAPIError
    }
  };

  const handleReject = async () => {
    try {
      await rejectTicketAsync({ ticketId, rejectionReason: notes });
      toast.success('Ticket sent back to crew.');
      setNotes('');
      setRejectOpen(false);
    } catch (err) {
      console.log('Error rejecting ticket', err);
      // error handled inside hook via handleAPIError
    }
  };

  return (
    <div className="space-y-3 pt-2 border-t border-border">
      {/* Rejection notes — slides in above the buttons when open */}
      {rejectOpen && (
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Rejection notes <span className="text-destructive">*</span>
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe what the crew needs to fix before resubmitting…"
            className={cn(
              'w-full rounded-lg border border-border bg-background px-3 py-2.5',
              'text-sm text-foreground placeholder:text-muted-foreground',
              'resize-none focus:outline-none focus:ring-1 focus:ring-destructive',
              'transition-colors',
            )}
          />
        </div>
      )}

      <div className="flex gap-2">
        {rejectOpen ? (
          <>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                setRejectOpen(false);
                setNotes('');
              }}
              className="flex-1 h-9 rounded-lg text-sm font-medium bg-muted hover:bg-muted/80 text-muted-foreground border border-border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={notes.trim().length === 0 || isBusy}
              onClick={handleReject}
              className={cn(
                'flex-1 h-9 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2',
                notes.trim().length > 0 && !isBusy
                  ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                  : 'bg-muted text-muted-foreground cursor-not-allowed',
              )}
            >
              {isRejecting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <XCircle className="w-3.5 h-3.5" />
              )}
              {isRejecting ? 'Sending…' : 'Confirm rejection'}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={isBusy}
              onClick={handleVerify}
              className={cn(
                'flex-1 h-9 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2',
                'bg-green-600 hover:bg-green-700 text-white',
                isBusy && 'opacity-50 cursor-not-allowed',
              )}
            >
              {isApproving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5" />
              )}
              {isApproving ? 'Verifying…' : 'Verify'}
            </button>

            <button
              type="button"
              disabled={isBusy}
              onClick={() => setRejectOpen(true)}
              className={cn(
                'flex-1 h-9 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2',
                'bg-muted hover:bg-muted/80 text-foreground border border-border',
                isBusy && 'opacity-50 cursor-not-allowed',
              )}
            >
              <XCircle className="w-3.5 h-3.5" />
              Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const TicketReviewTab: React.FC<TicketReviewTabProps> = ({ ticket }) => {
  const review = ticket?.aiReview;

  const terminalStatuses: TicketStatus[] = [
    TicketStatus.COMPLETED,
    TicketStatus.REJECTED,
  ];
  const isFieldComplete = ticket?.status === TicketStatus.FIELD_COMPLETE;
  const showAdminActions =
    isFieldComplete && !!review && !terminalStatuses.includes(ticket.status);

  // ── Not yet completed ──────────────────────────────────────────────────────
  if (!isFieldComplete && !terminalStatuses.includes(ticket.status)) {
    return (
      <EmptyState
        icon={Clock}
        title="Awaiting Crew Completion"
        description="AI analysis will begin once the crew marks this ticket as complete."
      />
    );
  }

  // ── Completed but review not yet ready ────────────────────────────────────
  if (!review) {
    return (
      <EmptyState
        icon={Bot}
        title="AI Analysis In Progress"
        description="The ticket is complete. Our AI is reviewing the details — check back shortly."
        pulse
      />
    );
  }

  // ── Failed — show error banner + admin footer, skip all AI sections ────────
  if (review.processingStatus === 'FAILED') {
    return (
      <div className="space-y-6 py-4">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
            <p className="text-sm font-semibold text-destructive">
              AI Review Failed
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            The AI pipeline encountered an error and could not complete the
            review.
          </p>
        </div>

        {showAdminActions && <AdminActionFooter ticketId={ticket.id} />}
      </div>
    );
  }

  // ── Severity config ────────────────────────────────────────────────────────
  const severityKey = (review.severityAssessment as Severity) ?? 'low';
  const severity = SEVERITY_CONFIG[severityKey] ?? SEVERITY_CONFIG.low;

  // ── Full review ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 py-4 w-full min-w-0">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 min-w-0">
        <div className="min-w-0 space-y-1">
          <p className="text-xs text-muted-foreground">
            Reviewed {formatDateTime(review.reviewedAt)}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="w-3.5 h-3.5 text-green-500 shrink-0" />
            <span>
              Confidence:{' '}
              <span className="font-semibold text-foreground">
                {((review.confidenceScore ?? 0) * 100).toFixed(1)}%
              </span>
            </span>
          </div>
        </div>
        <Badge
          variant="outline"
          className={cn(
            'shrink-0 gap-1 text-xs font-medium rounded-full',
            severity.className,
          )}
        >
          <Flame className="w-3 h-3" />
          {severity.label}
        </Badge>
      </div>

      {/* ── Partial warning ──────────────────────────────────────────────── */}
      {review.processingStatus === 'PARTIAL' && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Some parts of the AI review could not be completed. Results below
            may be partial.
          </p>
        </div>
      )}

      {/* ── Summary ─────────────────────────────────────────────────────── */}
      <Section icon={Lightbulb} iconClass="text-blue-500" title="AI Summary">
        <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm leading-relaxed text-foreground">
          {review.summary || (
            <span className="text-muted-foreground italic">
              No summary available.
            </span>
          )}
        </div>
      </Section>

      {/* ── Issue + Action ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
        <Section
          icon={BarChart2}
          iconClass="text-purple-500"
          title="Issue Identified"
        >
          <p className="text-xs text-muted-foreground leading-relaxed">
            {review.issueIdentified || (
              <span className="italic">None identified.</span>
            )}
          </p>
        </Section>
        <Section
          icon={ClipboardList}
          iconClass="text-cyan-500"
          title="Recommended Action"
        >
          <p className="text-xs text-muted-foreground leading-relaxed">
            {review.recommendedAction || (
              <span className="italic">No action recommended.</span>
            )}
          </p>
        </Section>
      </div>

      {/* ── Data completeness ────────────────────────────────────────────── */}
      <Section
        icon={CheckCircle}
        iconClass="text-primary"
        title="Data Completeness"
      >
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  completenessColor(review.dataCompletenessScore ?? 0),
                )}
                style={{
                  width: `${((review.dataCompletenessScore ?? 0) / 5) * 100}%`,
                }}
              />
            </div>
            <span className="text-xs font-semibold text-foreground tabular-nums shrink-0">
              {review.dataCompletenessScore ?? 0}/5
            </span>
          </div>
          {(review.dataCompletenessScore ?? 0) < 4 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 italic">
              Low completeness may indicate missing details or unclear
              observations.
            </p>
          )}
        </div>
      </Section>

      {/* ── Potential discrepancies ──────────────────────────────────────── */}
      {Array.isArray(review.potentialDiscrepancies) &&
        (review.potentialDiscrepancies as string[]).length > 0 && (
          <Section
            icon={AlertCircle}
            iconClass="text-destructive"
            title="Potential Discrepancies"
          >
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <ul className="space-y-1">
                {(review.potentialDiscrepancies as string[]).map((item, i) => (
                  <li
                    key={i}
                    className="text-xs text-foreground flex items-start gap-2"
                  >
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-destructive shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        )}

      {/* ── Crew notes ──────────────────────────────────────────────────── */}
      <Section
        icon={MessageSquareText}
        iconClass="text-muted-foreground"
        title="Original Crew Notes"
      >
        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
          {ticket?.notes?.length > 0 ? (
            ticket.notes.map((note, i) => (
              <div key={i} className="text-xs text-foreground">
                <span className="text-muted-foreground font-medium">
                  [{formatDateTime(note?.createdAt)}]
                </span>{' '}
                {note.content}
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No crew notes provided.
            </p>
          )}
        </div>
      </Section>

      {/* ── Image observations ──────────────────────────────────────────── */}
      <Section
        icon={Image}
        iconClass="text-purple-500"
        title="Image Observations"
      >
        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
          {(review.imageDescriptions as string[])?.length > 0 ? (
            (review.imageDescriptions as string[]).map((desc, i) => (
              <p key={i} className="text-xs text-foreground leading-relaxed">
                {desc}
              </p>
            ))
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No images were provided or analysed.
            </p>
          )}
        </div>
      </Section>

      {/* ── Processing errors (PARTIAL) ──────────────────────────────────── */}
      {review.processingStatus === 'PARTIAL' &&
        (review.processingErrors as string[])?.length > 0 && (
          <Section
            icon={AlertCircle}
            iconClass="text-destructive"
            title="Processing Errors"
          >
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
              <ul className="space-y-1">
                {(review.processingErrors as string[]).map((err, i) => (
                  <li key={i} className="text-xs text-foreground font-mono">
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        )}

      {/* ── Admin action footer ──────────────────────────────────────────── */}
      {showAdminActions && <AdminActionFooter ticketId={ticket.id} />}
    </div>
  );
};
